import { MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH } from "../tabularColumnWidths.ts";
import type { GridApiPremium, GridColDef, GridSortCellParams } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import type { Maybe } from "@entrilia/shared/types";
import { getRowNode } from "@entrilia/shared/utilities/dataGridApiHelper";
import type { TabularDataCell, TabularRow } from "../../../../../../../../../api/types/svTypes";
import { getCellFromRow } from "../dimension-column/buildDimensionColumnHelper";
import type { BaseColumnField } from "./buildColumnBase.types";

export const buildColumnBase = (field: BaseColumnField, apiRef: RefObject<GridApiPremium | null>): GridColDef => {
  return {
    field: field.guid,
    headerName: field.headerName,
    headerAlign: field.align,
    align: field.align,
    minWidth: MIN_COLUMN_WIDTH,
    maxWidth: MAX_COLUMN_WIDTH,
    type: "string",
    groupable: field.groupable,
    aggregable: !field.hideAggregation,
    // We use the custom ColumnMenuFreezeItem component
    // to override the default pinning behavior and only show the "pin to left" option in the column menu.
    pinnable: false,
    filterable: false,
    hideable: false,
    sortable: true,
    sortComparator: getSortComparator(apiRef, field.guid),
    groupingValueGetter: (_, row: TabularRow, column: GridColDef) =>
      getGroupingValue(getCellFromRow(row, column.field)) as Maybe<string>,
    valueGetter: (_, row: TabularRow, column: GridColDef) => getCellFromRow(row, column.field),
  };
};

export const getGroupingValue = (cell: TabularDataCell | undefined) => {
  return !isEmpty(cell?.value) ? cell.value : "";
};

export const refineComparisonResult = (
  currentGroupSorting: "asc" | "desc",
  topLevelGroupSorting: "asc" | "desc",
  comparisonResult: number
) => {
  const ascMultiplier = topLevelGroupSorting === "asc" ? 1 : -1;
  const descMultiplier = ascMultiplier * -1;

  return comparisonResult * (currentGroupSorting === "asc" ? ascMultiplier : descMultiplier);
};

const getSortComparator =
  (apiRef: RefObject<GridApiPremium | null>, fieldGuid: string) =>
  <V extends TabularDataCell | undefined>(v1: V, v2: V, cell1: GridSortCellParams<V>, cell2: GridSortCellParams<V>) => {
    if (!apiRef.current) {
      return compareValues(v1?.value, v2?.value);
    }
    const isGroupRow = cell1.rowNode.type === "group";

    const cell1value = getRowNode(apiRef, cell1.id);
    const cell2value = getRowNode(apiRef, cell2.id);
    if (isGroupRow && cell1value && cell2value) {
      if (cell1value?.type === "group" && cell2value?.type === "group") {
        const comparisonResult = compareValues(cell1value.groupingKey, cell2value.groupingKey);

        if (!isTopLevelGroup(cell1value.depth)) {
          const sortModel = apiRef.current.getSortModel();
          const currentGroupSorting = sortModel.find((sort) => sort.field === cell1value.groupingField)?.sort ?? "asc";
          const topLevelGroupSorting = sortModel.find((sort) => sort.field === fieldGuid)?.sort ?? "asc";

          return refineComparisonResult(currentGroupSorting, topLevelGroupSorting, comparisonResult);
        }

        return comparisonResult;
      }
    }

    return compareValues(v1?.value, v2?.value);
  };

const compareValues = (v1: unknown, v2: unknown) => {
  if (typeof v1 === "number" && typeof v2 === "number") {
    return v1 - v2;
  }
  return (v1?.toString() ?? "").localeCompare(v2?.toString() ?? "");
};

const isEmpty = (value: unknown): value is null | undefined => value === null || value === undefined;

const isTopLevelGroup = (depth: number) => depth === 0;
